import type { ReactNode } from 'react';
import { Check, Loader2, RotateCw, UserCheck, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCaseNotifications, useForceAccept } from '@/lib/notifications/use-notifications';
import type { INotificationStatusRow } from '@/lib/notifications/types';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { cn } from '@/lib/cn';
import { callbackRequested, offerKindKey } from './pnr-steps';
import { CallbackRequest } from './callback-request';
import { NotificationStatusBadges } from './notification-status-badges';
import { ForceAcceptDialog } from './force-accept-dialog';
import { FieldLabel } from './airport-ui';

// Offer & acceptance for ONE PNR, inside the Operation Center card (the surface
// the operator actually works a passenger from). The acceptance gate used to be
// visible only on the roster GRID's notification cell — open the passenger's
// card and the gate disappeared: no way to see whether the offer went out, no
// way to force-accept THIS PNR (the only forces were case-/disruption-wide), so
// a held hotel that waited on a passenger who never answers (demo manifests
// carry no contact at all) read as "stuck" with no way forward.
//
// One `useCaseNotifications(caseUrn)` query backs this AND every roster cell
// for the case (React Query dedups); the acceptance state refreshes live over
// the case WS room the drawer already joins. Force-accept is the SAME per-PNR
// SEAM-1 `source=OPERATOR_FORCE` the roster cell uses — supervisor-gated,
// audited, nothing new past the ingress. Resending the offer / calling the
// passenger / fixing a contact live in the comms card beside this one.

/** One gate on the offer's way to the passenger. */
function GateStep({
  state,
  label,
  sub,
}: {
  state: 'done' | 'wait' | 'alert' | 'idle';
  label: string;
  sub?: string | undefined;
}) {
  const mark =
    state === 'done' ? (
      <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-emerald-500 text-white">
        <Check className="h-2.5 w-2.5" aria-hidden />
      </span>
    ) : state === 'alert' ? (
      <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-rose-500 text-white">
        <X className="h-2.5 w-2.5" aria-hidden />
      </span>
    ) : (
      <span
        className={cn(
          'box-border inline-flex h-[18px] w-[18px] rounded-full border-2',
          state === 'wait' ? 'border-dashed border-amber-500' : 'border-border',
        )}
      />
    );
  return (
    <div className="flex items-center gap-2.5">
      <span className="inline-flex w-[18px] shrink-0">{mark}</span>
      <span className="w-20 text-[13px] font-semibold">{label}</span>
      {sub ? <span className="min-w-0 truncate text-xs text-muted-foreground">{sub}</span> : null}
    </div>
  );
}

/**
 * The offer's journey to the passenger — proposed → sent → delivered → reply —
 * drawn as the gate rail the operator reasons over (which gate is it stuck at?).
 */
function GateTimeline({
  row,
  offerKind,
}: {
  row: INotificationStatusRow;
  offerKind?: string | undefined;
}) {
  const { t } = useTranslation('pnr');
  const sentState =
    row.deliveryState === 'unknown' ? 'idle' : row.deliveryState === 'pending' ? 'wait' : row.deliveryState === 'failed' ? 'alert' : 'done';
  const deliveredState =
    row.deliveryState === 'delivered' ? 'done' : row.deliveryState === 'failed' ? 'alert' : 'idle';
  const replyState =
    row.acceptanceState === 'accepted' ? 'done' : row.acceptanceState === 'declined' ? 'alert' : 'wait';
  const channel = row.channel ? t(`steps.passenger.channel.${row.channel.toLowerCase()}`) : undefined;
  return (
    <div className="space-y-1.5 py-1" data-testid="offer-gate-timeline">
      <GateStep
        state="done"
        label={t('offerTimeline.proposed')}
        sub={t(`steps.passenger.offerKind.${offerKindKey(offerKind)}`)}
      />
      <GateStep
        state={sentState}
        label={t('offerTimeline.sent')}
        sub={
          sentState === 'alert'
            ? t('offerTimeline.failed')
            : sentState === 'idle'
            ? t('offerTimeline.notYet')
            : sentState === 'wait'
              ? t('offerTimeline.pending')
              : channel
                ? t('offerTimeline.sentVia', { channel })
                : undefined
        }
      />
      <GateStep
        state={deliveredState}
        label={t('offerTimeline.delivered')}
        sub={deliveredState === 'alert' ? t('offerTimeline.failed') : deliveredState === 'idle' ? t('offerTimeline.notYet') : undefined}
      />
      <GateStep
        state={replyState}
        label={t('offerTimeline.reply')}
        sub={t(
          replyState === 'done'
            ? 'offerTimeline.accepted'
            : replyState === 'alert'
              ? 'offerTimeline.declined'
              : 'offerTimeline.waiting',
        )}
      />
    </div>
  );
}

function errMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message;
  }
  return fallback;
}

export function OfferAcceptanceCard({
  group,
  onCall,
  calling = false,
}: {
  group: DisruptionPnrGroupRow;
  /** «Prefiero que me llamen»: the call (the passenger card owns it). */
  onCall?: (() => void) | undefined;
  calling?: boolean;
}) {
  const { t } = useTranslation('pnr');
  const { rowFor, isLoading, isError, refetch } = useCaseNotifications(group.caseUrn);
  const force = useForceAccept(group.caseUrn);
  const row = rowFor(group.subCaseUrn);

  const doForce = (reason: string): void => {
    if (!row) return;
    force.mutate({
      subCaseUrn: group.subCaseUrn,
      body: { offerHash: row.offerHash ?? '', ...(reason ? { reason } : {}) },
    });
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <p className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="offer-acceptance-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> {t('offer.loading')}
      </p>
    );
  } else if (isError) {
    body = (
      <p className="flex items-center gap-2 text-sm text-destructive" data-testid="offer-acceptance-error">
        {t('notification.statusUnavailable')}
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          data-testid="offer-acceptance-retry"
        >
          <RotateCw className="h-3 w-3" aria-hidden /> {t('notification.retry')}
        </button>
      </p>
    );
  } else if (!row || row.acceptanceState === 'none') {
    body = (
      <p className="text-sm text-muted-foreground" data-testid="offer-acceptance-none">
        {t('offer.none')}
      </p>
    );
  } else {
    const awaiting = row.acceptanceState === 'awaiting';
    const declined = row.acceptanceState === 'declined';
    body = (
      <div className="space-y-2" data-testid="offer-acceptance-status">
        <NotificationStatusBadges
          deliveryState={row.deliveryState}
          acceptanceState={row.acceptanceState}
          channel={row.channel}
        />
        <GateTimeline row={row} offerKind={group.offer?.kind} />
        {awaiting ? (
          <p className="text-sm text-muted-foreground">{t('offer.awaiting')}</p>
        ) : declined ? (
          <p className="text-sm text-muted-foreground">{t('offer.declined')}</p>
        ) : (
          <p className="text-sm text-muted-foreground">{t('offer.accepted')}</p>
        )}
      </div>
    );
  }

  return (
    <section
      className="rounded-lg border border-border p-3"
      aria-label={t('offer.ariaLabel')}
      data-testid="offer-acceptance-card"
    >
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <FieldLabel className="flex items-center gap-1.5">
          <UserCheck className="h-3.5 w-3.5" aria-hidden />
          {t('offer.title')}
        </FieldLabel>
        {/* The supervisor force lives on the card's bar, never among the facts. */}
        {row && row.acceptanceState === 'awaiting' ? (
          <ForceAcceptDialog
            key={row?.offerHash ?? 'missing-offer'}
            groupLabel={group.pnrLabel}
            pending={force.isPending}
            offerHashMissing={!row.offerHash}
            error={force.isError ? errMessage(force.error, t('notification.requestFailed')) : null}
            onConfirm={doForce}
          />
        ) : null}
      </div>
      {body}
      {callbackRequested(group) ? (
        <div className="mt-3">
          <CallbackRequest group={group} onCall={onCall} calling={calling} />
        </div>
      ) : group.lastOfferResponse?.response === 'DECLINED' && group.lastOfferResponse.declineIntent ? (
        <div className="mt-3 rounded border border-border p-3 text-sm" data-testid="hotel-decline-intent">
          <p className="font-semibold">{t('hotelDecline.title')}</p>
          <p>{t(`hotelDecline.${group.lastOfferResponse.declineIntent}`)}</p>
          <p className="text-xs text-muted-foreground">{t('hotelDecline.preferenceOnly')}</p>
          {group.lastOfferResponse.reason ? <p className="mt-2 whitespace-pre-wrap">{group.lastOfferResponse.reason}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
